import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Correspondence } from '@bumble/app/__generated__/data-contracts';
import { TicketFileInput } from '@bumble/app/__generated__/Api';
import { TicketAttachmentUploader } from './TicketAttachmentUploader';

interface CorrespondenceDialogProps {
  ticketID: string;
  isReplying: boolean;
  onReplySent: () => void;
  onClose: () => void;
}

export const CorrespondenceDialog = ({
  ticketID,
  isReplying,
  onReplySent,
  onClose,
}: CorrespondenceDialogProps) => {
  const api = useApi();
  const qc = useQueryClient();

  const { isLoading, data: repliesRes } = useQuery(
    ['replies', ticketID],
    api.getCorrespondenceApiV1GetCorrespondenceGet.bind(null, { ticket_id: ticketID }),
    { enabled: !!ticketID }
  );

  const replies: Correspondence[] = useMemo(
    () => (Array.isArray(repliesRes?.data) ? (repliesRes!.data as Correspondence[]) : []),
    [repliesRes]
  );

  const [reply, setReply] = useState('');
  const [attachments, setAttachments] = useState<TicketFileInput[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const MAX_LEN = 8000;
  const remaining = Math.max(0, MAX_LEN - reply.length);
  const canSend = reply.trim().length > 0 && reply.length <= MAX_LEN;

  useEffect(() => {
    setReply('');
    setAttachments([]);
  }, [ticketID, isReplying]);

  const replyCreate = useMutation(api.addCorrespondenceApiV1AddCorrespondencePost, {
    onSuccess: () => {
      toast.success('Reply sent successfully.');
      setReply('');
      setAttachments([]);
      onReplySent();
    },
    onError: apiError,
  });

  const handleSend = () => {
    if (!canSend || replyCreate.isLoading || isUploadingAttachments) return;
    replyCreate.mutate({
      ticket_id: ticketID,
      correspondence: reply,
      uploaded_files: attachments,
    });
  };

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Typography fontWeight="bold" mb={2}>
            Reply
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            id="reply"
            variant="outlined"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            autoFocus
            inputProps={{ maxLength: MAX_LEN }}
            helperText={`Maximum 8000 characters (${remaining} remaining)`}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography fontWeight="bold" mb={2}>
            Attachments
          </Typography>
          <TicketAttachmentUploader
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            onUploadingChange={setIsUploadingAttachments}
            disabled={replyCreate.isLoading}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={onClose} disabled={replyCreate.isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!canSend || replyCreate.isLoading || isUploadingAttachments}
        >
          {replyCreate.isLoading ? 'Sending…' : 'Send'}
        </Button>
      </Box>
    </Box>
  );
};
